// SwitcherPopup.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './SwitcherPopup.css';

// Components
import LogoutButton from 'main/sidebar/switcher/popup/logout/LogoutButton';
import SwitcherPopupHeader from 'main/sidebar/switcher/popup/header/SwitcherPopupHeader';
import WorkspaceList from 'main/sidebar/switcher/popup/workspacelist/WorkspaceList';

/**
 * SwitcherPopup
 * 
 * Renders the popup component for the sidebar switcher.
 * 
 * @param {function} onWorkspaceSelect - Handler for workspace selection.
 * @param {function} onWorkspaceAdd - Handler for adding a new workspace.
 * @param {function} onInviteClick - Handler for inviting members to the workspace.
 * @param {function} onLogout - Handler for user logout.
 *  
 * @returns {ReactNode} - SwitcherPopup.
 */
const SwitcherPopup = React.memo(({
    onWorkspaceSelect,
    onWorkspaceAdd,
    onPublishTemplate,
    onPlanClick,
    onSettingsClick,
    onInviteClick,
    onLogout,
    workspaces = [],
    activeWorkspaceKey = null,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // User Context
    const { user, isSuperUser } = useUser();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // ----- DEVICE DETECTION ----- //

   const isMobile = useIsMobile();

    // ----- MEMOS ----- //

    // Active workspace fallback used by popup actions.
    const panelWorkspace = useMemo(() => {
        if (activeWorkspace?.key) return activeWorkspace;
        if (!activeWorkspaceKey) return null;
        return workspaces.find((workspace) => workspace?.key === activeWorkspaceKey || workspace?.id === activeWorkspaceKey) || null;
    }, [activeWorkspace, activeWorkspaceKey, workspaces]);

    // Publish permission flag for template actions.
    const canPublishTemplate = isSuperUser;

    // ----- HANDLERS ----- //

    /**
     * Opens settings actions for the active popup workspace.
     */
    const handleSettingsClick = () => {
        if (typeof onSettingsClick === 'function') {
            onSettingsClick(panelWorkspace || null);
        }
    };

    /**
     * Opens invite actions for the active popup workspace.
     */
    const handleInviteClick = () => {
        if (typeof onInviteClick === 'function') {
            onInviteClick(panelWorkspace || null);
        }
    };

    /**
     * Opens billing plan actions for the active popup workspace.
     */
    const handlePlanClick = () => {
        if (typeof onPlanClick === 'function') {
            onPlanClick(panelWorkspace || null);
        }
    };

    // ----- RENDER ----- //

    return (
        <div
            className="switcher-popup-container"
            style={{
                background: theme.panel,
            }}
        >
            {/* HEADER */}
            <SwitcherPopupHeader
                onPlanClick={handlePlanClick}
                onSettingsClick={handleSettingsClick}
                onInviteClick={handleInviteClick}
            />

            {/* SPACES LABEL */}
            <div className="switcher-popup-spaces-label-wrapper">
                MY SPACES
            </div>

            {/* SPACES LIST */}
            <WorkspaceList
                onSelect={onWorkspaceSelect}
                onAdd={onWorkspaceAdd}
                onPublishTemplate={onPublishTemplate}
                canPublishTemplate={canPublishTemplate}
                workspaces={workspaces}
                activeWorkspaceKey={activeWorkspaceKey}
            />

            {/* FOOTER */}
            <div
                className="switcher-popup-footer"
                style={{
                    borderTopColor: theme.backgroundMuted,
                    color: theme.foregroundMuted,
                }}
            >
                <LogoutButton onClick={onLogout} />
            </div>
        </div>
    );

});

// Dev Tools
SwitcherPopup.displayName = 'SwitcherPopup';

export default SwitcherPopup;

